import type { MouseEvent as ReactMouseEvent } from 'react'
import { Box, IconButton, Tooltip, Chip, CircularProgress, useTheme, alpha } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { FolderOpen, RotateCcw, HardDrive, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../utils/dateUtils'
import { Archive } from '../types'
import { getArchiveType } from '../utils/archiveGrouping'

interface ArchiveCardProps {
  archive: Archive
  onView: (archive: Archive) => void
  /** Click anywhere on the row: open the archive's detail page. */
  onOpen?: (archive: Archive) => void
  /** Where the row leads, so the name is a real link (new tab, copy link). */
  openHref?: string
  /** Omit when the user cannot restore from this repository. */
  onRestore?: (archive: Archive) => void
  onMount: (archive: Archive) => void
  // An agent-executed repository lives on the agent; the server cannot mount it.
  mountHidden?: boolean
  onDelete: (archive: Archive) => void
  /** The delete job is queued or running (#1197): the row says so and its delete is disabled. */
  isDeleting?: boolean
  mountDisabled?: boolean
  canDelete?: boolean
}

export default function ArchiveCard({
  archive,
  onView,
  onOpen,
  openHref,
  onRestore,
  onMount,
  mountHidden = false,
  onDelete,
  isDeleting = false,
  mountDisabled = false,
  canDelete = true,
}: ArchiveCardProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isManual = getArchiveType(archive) === 'manual'
  const archiveTime = archive.start || archive.time
  const desktopGridTemplate = 'minmax(0, 1fr) 76px minmax(180px, 220px) 132px'

  const nameSx = {
    display: 'block',
    textDecoration: 'none',
    fontFamily: '"JetBrains Mono","Fira Code",ui-monospace,monospace',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'text.primary',
    textUnderlineOffset: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    '@media (max-width: 767px)': { gridColumn: 1, gridRow: 1 },
  }

  const iconBtnSx = (color: string) => ({
    width: 28,
    height: 28,
    borderRadius: 1.5,
    color: alpha(color, 0.75),
    '&:hover': {
      bgcolor: alpha(color, isDark ? 0.12 : 0.09),
      color: color,
    },
    '&.Mui-disabled': { opacity: 0.28 },
  })

  return (
    <Box
      data-testid="archive-row"
      // A click anywhere on the row is a convenience; the archive name is
      // the real control, a link, which is what the keyboard and assistive
      // technology follow. The row must not claim a button role of its own:
      // a button's children are presentational, so a focusable link inside
      // one has no node in the accessibility tree.
      onClick={onOpen ? () => onOpen(archive) : undefined}
      sx={{
        cursor: onOpen ? 'pointer' : 'default',
        display: 'grid',
        gridTemplateColumns: desktopGridTemplate,
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.125,
        borderBottom: '1px solid',
        borderBottomColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.04),
        transition: 'all 150ms ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, isDark ? 0.04 : 0.03),
          // The row opens the archive: say so the way a link does.
          ...(onOpen && {
            '& .archive-name': { textDecoration: 'underline', color: 'primary.main' },
          }),
        },
        '@media (max-width: 767px)': {
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gridTemplateRows: 'auto auto',
          gap: 0.5,
          px: 1.75,
          py: 1.25,
        },
      }}
    >
      {/* Archive name: a real link when the route is known, so middle
          click and "open in a new tab" work; the row handles plain clicks */}
      {openHref ? (
        <Box
          component={RouterLink}
          to={openHref}
          className="archive-name"
          title={archive.name}
          onClick={(e: ReactMouseEvent) => e.stopPropagation()}
          sx={nameSx}
        >
          {archive.name}
        </Box>
      ) : (
        <Box className="archive-name" title={archive.name} sx={nameSx}>
          {archive.name}
        </Box>
      )}

      {/* Type chip + Date (share row 2 on mobile) */}
      <Box
        sx={{
          display: 'contents',
          '@media (max-width: 767px)': {
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            gridColumn: 1,
            gridRow: 2,
            minWidth: 0,
          },
        }}
      >
        {isDeleting && (
          <Chip
            icon={<CircularProgress size={10} color="inherit" />}
            label={t('common.buttons.deleting')}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              bgcolor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.1),
              color: 'error.main',
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, isDark ? 0.3 : 0.2),
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-icon': { ml: 0.75, color: 'inherit' },
            }}
          />
        )}
        <Chip
          label={
            isManual ? t('archivesList.manualAbbr', 'MAN') : t('archivesList.scheduledAbbr', 'SCH')
          }
          size="small"
          sx={{
            height: 18,
            justifySelf: 'start',
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            bgcolor: isManual
              ? alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1)
              : alpha(theme.palette.success.main, isDark ? 0.18 : 0.1),
            color: isManual ? 'primary.main' : 'success.main',
            border: '1px solid',
            borderColor: isManual
              ? alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)
              : alpha(theme.palette.success.main, isDark ? 0.3 : 0.2),
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Box
          component="span"
          sx={{
            fontSize: '0.72rem',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatDate(archiveTime)}
        </Box>
      </Box>

      {/* Actions — always visible */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          minWidth: 0,
          justifyContent: 'flex-end',
          '@media (max-width: 767px)': {
            gridColumn: 2,
            gridRow: '1 / 3',
            alignSelf: 'center',
          },
        }}
      >
        <Tooltip title={t('archiveCard.viewContents')} arrow>
          <IconButton
            size="small"
            onClick={() => onView(archive)}
            aria-label={t('archiveCard.viewContents')}
            sx={iconBtnSx(theme.palette.success.main)}
          >
            <FolderOpen size={15} />
          </IconButton>
        </Tooltip>

        {onRestore && (
          <Tooltip title={t('archiveCard.restore')} arrow>
            <IconButton
              size="small"
              onClick={() => onRestore(archive)}
              aria-label={t('archiveCard.restore')}
              sx={iconBtnSx(theme.palette.warning.main)}
            >
              <RotateCcw size={15} />
            </IconButton>
          </Tooltip>
        )}

        {!mountHidden && (
          <Tooltip title={t('archiveCard.mount')} arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => onMount(archive)}
                disabled={mountDisabled}
                aria-label={t('archiveCard.mount')}
                sx={iconBtnSx(theme.palette.info.main)}
              >
                <HardDrive size={15} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {canDelete && (
          <Tooltip
            title={isDeleting ? t('common.buttons.deleting') : t('archiveCard.delete')}
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={() => onDelete(archive)}
                disabled={isDeleting}
                aria-label={t('archiveCard.delete')}
                sx={iconBtnSx(theme.palette.error.main)}
              >
                <Trash2 size={15} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}
